import React, { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  Alert,
  Button,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NumberInput,
  Select,
  SelectList,
  SelectOption,
} from '@patternfly/react-core';
import type {
  GpuBudgetCatalogKind,
  GpuBudgetHoursRule,
  GpuBudgetLine,
  GpuBudgetLineCreateRequest,
  GpuBudgetLineUpdateRequest,
} from '../../../../types/models/GpuBudget';
import { ProductDescription } from '../Start/ServerStep';
import { formatBasisPrice, formatCount, kindCopy, unitWord } from './budgetLineKinds';
import {
  BUDGET_HOURS_HELP,
  BUDGET_PRODUCT_GONE,
  BUDGET_PRODUCT_NOT_OFFERED,
  BUDGET_REMOVED_PRODUCT,
  BUDGET_STATUS_PRICE_UNAVAILABLE,
} from './gpuBudgetCopy';
import type { ModalFeedback } from './gpuBudgetFeedback';
import { otherFieldErrors } from './gpuBudgetFeedback';

/** What the modal submits: the full body on add, only the changed fields on edit. */
export type GpuBudgetLineSubmit =
  | { mode: 'add'; body: GpuBudgetLineCreateRequest }
  | { mode: 'edit'; line: GpuBudgetLine; body: GpuBudgetLineUpdateRequest };

interface GpuBudgetLineModalProps {
  /** The catalog kind the line belongs to: every bound, default, unit and product comes from it. */
  kind: GpuBudgetCatalogKind;
  /** The stored line on edit; `null` on add. */
  line: GpuBudgetLine | null;
  /** Runs the write; the tab decides whether the modal closes. */
  onSubmit: (submit: GpuBudgetLineSubmit) => Promise<ModalFeedback>;
  onClose: () => void;
}

/** The fields this modal shows (their backend field errors go under them; any other goes in the Alert). */
const LINE_FIELDS = ['product_id', 'quantity', 'hours_per_month'] as const;

/** An integer input's value: `''` while emptied. */
type IntValue = number | '';

interface Bounds {
  min: number;
  max: number;
}

const inBounds = (value: IntValue, bounds: Bounds): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= bounds.min && value <= bounds.max;

const clamp = (n: number, bounds: Bounds): number => Math.min(bounds.max, Math.max(bounds.min, n));

const boundsMessage = (bounds: Bounds): string =>
  `Must be between ${formatCount(bounds.min)} and ${formatCount(bounds.max)}.`;

interface BoundedNumberInputProps {
  id: string;
  value: IntValue;
  setValue: Dispatch<SetStateAction<IntValue>>;
  bounds: Bounds;
  /** Where a stepper starts from when the field is empty. */
  fallback: number;
  unit: string;
  /** What the value is, for the input's and the steppers' accessible names. */
  what: string;
  isInvalid: boolean;
  isDisabled: boolean;
  describedBy: string;
}

/**
 * A NumberInput over an integer that may be emptied. Functional updates only: PatternFly rewrites an
 * emptied field to '0' and fires onChange before onBlur within the same event, so a render closure can
 * hold a stale value. Blur and the steppers clamp to the bounds; a value set from outside (a stored
 * line) is shown unclamped, with its error, until the user touches it.
 */
const BoundedNumberInput: React.FunctionComponent<BoundedNumberInputProps> = ({
  id,
  value,
  setValue,
  bounds,
  fallback,
  unit,
  what,
  isInvalid,
  isDisabled,
  describedBy,
}) => {
  const step = (delta: number) =>
    setValue((cur) =>
      typeof cur === 'number' && !Number.isNaN(cur) ? clamp(Math.round(cur) + delta, bounds) : fallback,
    );
  return (
    <NumberInput
      value={value}
      min={bounds.min}
      max={bounds.max}
      onMinus={() => step(-1)}
      onPlus={() => step(1)}
      onChange={(event) => {
        const raw = (event.target as HTMLInputElement).value;
        setValue(raw === '' ? '' : Number(raw));
      }}
      onBlur={() =>
        setValue((cur) => (typeof cur === 'number' && !Number.isNaN(cur) ? clamp(Math.round(cur), bounds) : cur))
      }
      unit={unit}
      inputName={id}
      inputAriaLabel={what}
      minusBtnAriaLabel={`Decrease the ${what.toLowerCase()}`}
      plusBtnAriaLabel={`Increase the ${what.toLowerCase()}`}
      inputProps={{ id, 'aria-describedby': describedBy }}
      validated={isInvalid ? 'error' : 'default'}
      widthChars={Math.max(3, String(bounds.max).length + 1)}
      isDisabled={isDisabled}
    />
  );
};

/** "Average month" = the catalog default, "31-day month" = the catalog maximum (deduplicated). */
const hoursPresets = (rule: GpuBudgetHoursRule): { label: string; value: number }[] => {
  const presets = [
    { label: `Average month (${formatCount(rule.default)} h)`, value: rule.default },
    { label: `31-day month (${formatCount(rule.max)} h)`, value: rule.max },
  ];
  return presets.filter(
    (preset, index) =>
      preset.value >= rule.min &&
      preset.value <= rule.max &&
      presets.findIndex((other) => other.value === preset.value) === index,
  );
};

/**
 * Add or edit one budget line — ONE generic modal driven by the catalog kind, so a kind the frontend
 * has no copy for is fully editable too. Product (with its price hint and French description),
 * quantity (bounds, default and unit from `kind.quantity`) and, only when `kind.hours_per_month` is not
 * null, hours per month with two presets.
 *
 * Every submit is a saved write (no draft). Add POSTs the full body; edit PATCHes ONLY the fields that
 * differ from the stored line, and Submit stays disabled until one does (the backend refuses an empty
 * PATCH, and counts a field sent unchanged as a change). Edit fields start from the stored line
 * WITHOUT clamping: a value outside the catalog bounds (written by an older release) shows its error
 * so the user fixes it. Mounted only while open, so each opening starts clean.
 */
const GpuBudgetLineModal: React.FunctionComponent<GpuBudgetLineModalProps> = ({ kind, line, onSubmit, onClose }) => {
  const copy = kindCopy(kind.kind);
  const hoursRule = kind.hours_per_month;
  const storedProduct = line?.product ?? null;

  const [productId, setProductId] = useState<number | null>(line !== null ? (storedProduct?.id ?? null) : null);
  const [quantity, setQuantityValue] = useState<IntValue>(line !== null ? line.quantity : kind.quantity.default);
  const [hours, setHoursValue] = useState<IntValue>(
    hoursRule === null ? '' : line !== null ? (line.hours_per_month ?? '') : hoursRule.default,
  );
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string[]>([]);

  const clearFieldError = (field: string) =>
    setFieldErrors((current) => {
      if (!(field in current)) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });

  // Wrap the setters so that editing a field clears the backend's error for it.
  const setQuantity: Dispatch<SetStateAction<IntValue>> = (value) => {
    clearFieldError('quantity');
    setQuantityValue(value);
  };
  const setHours: Dispatch<SetStateAction<IntValue>> = (value) => {
    clearFieldError('hours_per_month');
    setHoursValue(value);
  };

  const selectedProduct = kind.products.find((product) => product.id === productId) ?? null;
  // On edit, the stored product may no longer be offered (or no longer exist): the Select starts on it.
  const storedUnlisted =
    line !== null && (storedProduct === null || !kind.products.some((product) => product.id === storedProduct.id));
  const showsStored = storedUnlisted && productId === (storedProduct?.id ?? null);
  const toggleText =
    selectedProduct?.label ?? (showsStored ? (storedProduct?.label ?? BUDGET_REMOVED_PRODUCT) : 'Select a product');
  const description = selectedProduct?.description ?? (showsStored ? (storedProduct?.description ?? null) : null);

  const quantityValid = inBounds(quantity, kind.quantity);
  const hoursValid = hoursRule === null || inBounds(hours, hoursRule);

  const changes: GpuBudgetLineUpdateRequest = {};
  if (line !== null) {
    if (productId !== null && productId !== (storedProduct?.id ?? null)) {
      changes.product_id = productId;
    }
    if (typeof quantity === 'number' && quantity !== line.quantity) {
      changes.quantity = quantity;
    }
    if (hoursRule !== null && typeof hours === 'number' && hours !== line.hours_per_month) {
      changes.hours_per_month = hours;
    }
  }
  const hasChanges = Object.keys(changes).length > 0;
  const canSubmit = !pending && quantityValid && hoursValid && (line === null ? productId !== null : hasChanges);

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    let submit: GpuBudgetLineSubmit;
    if (line === null) {
      if (productId === null || !inBounds(quantity, kind.quantity)) {
        return;
      }
      const body: GpuBudgetLineCreateRequest = { kind: kind.kind, product_id: productId, quantity };
      if (hoursRule !== null && inBounds(hours, hoursRule)) {
        body.hours_per_month = hours;
      }
      submit = { mode: 'add', body };
    } else {
      submit = { mode: 'edit', line, body: changes };
    }
    setPending(true);
    setAlert([]);
    setFieldErrors({});
    const feedback = await onSubmit(submit);
    if (feedback.close) {
      onClose();
      return;
    }
    setPending(false);
    setFieldErrors(feedback.fieldErrors);
    setAlert([
      ...(feedback.alert !== null ? [feedback.alert] : []),
      ...otherFieldErrors(feedback.fieldErrors, LINE_FIELDS),
    ]);
  };

  const quantityUnit = unitWord(kind.quantity.unit, typeof quantity === 'number' ? quantity : kind.quantity.default);
  const quantityError = fieldErrors.quantity ?? (quantityValid ? null : boundsMessage(kind.quantity));
  const hoursError =
    hoursRule === null ? null : (fieldErrors.hours_per_month ?? (hoursValid ? null : boundsMessage(hoursRule)));
  const productError = fieldErrors.product_id ?? null;
  const title = line === null ? copy.addTitle : copy.editTitle;

  return (
    <Modal variant="medium" isOpen onClose={pending ? undefined : onClose} aria-labelledby="gpu-budget-line-title">
      <ModalHeader title={title} labelId="gpu-budget-line-title" />
      <ModalBody>
        {alert.length > 0 && (
          <Alert variant="danger" isInline title="Could not save the line" className="pf-v6-u-mb-md">
            {alert.map((text) => (
              <div key={text}>{text}</div>
            ))}
          </Alert>
        )}
        <Form
          id="gpu-budget-line-form"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <FormGroup label="Product" isRequired fieldId="gpu-budget-line-product">
            <Select
              id="gpu-budget-line-product-select"
              isOpen={isProductOpen}
              selected={productId ?? undefined}
              onSelect={(_event, value) => {
                if (typeof value === 'number') {
                  setProductId(value);
                  clearFieldError('product_id');
                }
                setIsProductOpen(false);
              }}
              onOpenChange={(open) => setIsProductOpen(open)}
              // Inside the dialog: a menu appended to <body> would sit outside the modal (hidden from
              // assistive technology by it).
              popperProps={{ appendTo: 'inline' }}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  id="gpu-budget-line-product"
                  onClick={() => setIsProductOpen(!isProductOpen)}
                  isExpanded={isProductOpen}
                  isDisabled={pending}
                  isFullWidth
                  status={productError !== null ? 'danger' : showsStored ? 'warning' : undefined}
                  aria-label="Choose the product of this line"
                  aria-describedby="gpu-budget-line-product-helper"
                >
                  {toggleText}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList aria-label="Products">
                {kind.products.length === 0 && (
                  <SelectOption value="none" isDisabled>
                    {copy.noProductsTooltip}
                  </SelectOption>
                )}
                {kind.products.map((product) => (
                  <SelectOption
                    key={product.id}
                    value={product.id}
                    isSelected={product.id === productId}
                    description={
                      product.unit_price_ht !== null
                        ? `${formatBasisPrice(product.unit_price_ht, kind.unit_price_basis)} excl. VAT`
                        : BUDGET_STATUS_PRICE_UNAVAILABLE
                    }
                  >
                    {product.label}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
            <ProductDescription description={description} />
            <FormHelperText>
              <HelperText id="gpu-budget-line-product-helper">
                {productError !== null ? (
                  <HelperTextItem variant="error">{productError}</HelperTextItem>
                ) : showsStored ? (
                  <HelperTextItem variant="warning">
                    {storedProduct === null ? BUDGET_PRODUCT_GONE : BUDGET_PRODUCT_NOT_OFFERED}
                  </HelperTextItem>
                ) : null}
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label={copy.quantityLabel} isRequired fieldId="gpu-budget-line-quantity">
            <BoundedNumberInput
              id="gpu-budget-line-quantity"
              value={quantity}
              setValue={setQuantity}
              bounds={kind.quantity}
              fallback={kind.quantity.default}
              unit={quantityUnit}
              what={copy.quantityLabel}
              isInvalid={quantityError !== null}
              isDisabled={pending}
              describedBy="gpu-budget-line-quantity-helper"
            />
            <FormHelperText>
              <HelperText id="gpu-budget-line-quantity-helper">
                {quantityError !== null ? (
                  <HelperTextItem variant="error">{quantityError}</HelperTextItem>
                ) : (
                  <HelperTextItem>
                    From {formatCount(kind.quantity.min)} to {formatCount(kind.quantity.max)}{' '}
                    {unitWord(kind.quantity.unit, kind.quantity.max)}.
                  </HelperTextItem>
                )}
              </HelperText>
            </FormHelperText>
          </FormGroup>

          {hoursRule !== null && (
            <FormGroup label="Hours per month" isRequired fieldId="gpu-budget-line-hours">
              <BoundedNumberInput
                id="gpu-budget-line-hours"
                value={hours}
                setValue={setHours}
                bounds={hoursRule}
                fallback={hoursRule.default}
                unit="h"
                what="Hours per month"
                isInvalid={hoursError !== null}
                isDisabled={pending}
                describedBy="gpu-budget-line-hours-helper"
              />
              <Flex spaceItems={{ default: 'spaceItemsMd' }} className="pf-v6-u-mt-sm">
                {hoursPresets(hoursRule).map((preset) => (
                  <FlexItem key={preset.value}>
                    <Button variant="link" isInline onClick={() => setHours(preset.value)} isDisabled={pending}>
                      {preset.label}
                    </Button>
                  </FlexItem>
                ))}
              </Flex>
              <FormHelperText>
                <HelperText id="gpu-budget-line-hours-helper">
                  {hoursError !== null && <HelperTextItem variant="error">{hoursError}</HelperTextItem>}
                  <HelperTextItem>{BUDGET_HOURS_HELP}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" type="submit" form="gpu-budget-line-form" isLoading={pending} isDisabled={!canSubmit}>
          {line === null ? 'Add' : 'Update'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={pending}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GpuBudgetLineModal;
